// src/pages/manage_reward_boxes_page.tsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../app/store";
import { Box } from "../modal/box";
import ItemSelect from "../components/item_select";
import Modal from "../components/modal";
import { toast } from "react-toastify";

import {
  getAllBoxes,
  resetGetAllBoxesStatus,
} from "../features/boxSlice";

import {
  assignRewardBox,
  resetAssignRewardStatus,
} from "../features/rewardSlice";

import { getImageSource } from "../utils/utils";

/* ========== Styled Components ========== */
const Container = styled.div`
  padding: 30px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  max-width: 640px;
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: 22px;
  color: #333;
  text-align: center;
  margin-bottom: 24px;
`;

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const Label = styled.label`
  width: 160px;
  font-weight: 600;
  font-size: 14px;
  color: #444;
  flex-shrink: 0;
`;

const ReadOnly = styled.div`
  flex: 1;
  padding: 10px 12px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e7e7e7;
  color: #222;
`;

const BoxDetails = styled.div`
  display: flex;
  align-items: center;
  padding: 14px;
  border-radius: 8px;
  background-color: #fff;
  border: 1px solid #eee;
  margin: 10px 0 16px;

  img {
    width: 80px;
    height: 80px;
    object-fit: contain;
    border-radius: 8px;
    margin-right: 16px;
    background: #fafafa;
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: 6px;

    div { font-size: 14px; color: #333; }
    strong { font-weight: 600; color: #222; }
  }
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background-color: ${({ disabled }) => (disabled ? "#9bbcf3" : "#007bff")};
  border: none;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ disabled }) => (disabled ? "#9bbcf3" : "#0569d3")};
  }
`;

/* ========== Props ========== */
interface ManageRewardBoxesPageProps {
  isOpen: boolean;
  onClose: () => void;
}

/* ========== Component ========== */
const ManageRewardBoxesPage: React.FC<ManageRewardBoxesPageProps> = ({ isOpen, onClose }) => {
  const dispatch: AppDispatch = useDispatch();

  // Rewards slice state
  const {
    selectedRewardBox,
    assignRewardStatus,
    assignRewardError,
  } = useSelector((s: RootState) => s.rewards);

  // Boxes slice state
  const { allBoxes, getAllBoxesStatus } = useSelector((s: RootState) => s.boxes);

  // Local selection
  const [selectedBox, setSelectedBox] = useState<Box | null>(null);

  // On open: fetch all boxes
  useEffect(() => {
    if (isOpen) {
      dispatch(getAllBoxes());
    }
  }, [dispatch, isOpen]);

  // When boxes load, preselect the box whose id matches selectedRewardBox.reward_id
  useEffect(() => {
    if (getAllBoxesStatus === "succeeded") {
      const rewardId = selectedRewardBox?.reward_id;
      const preSelected =
        rewardId != null
          ? allBoxes.find((b) => String(b.id) === String(rewardId)) || null
          : null;

      setSelectedBox(preSelected);
      dispatch(resetGetAllBoxesStatus());
    }
  }, [getAllBoxesStatus, allBoxes, selectedRewardBox, dispatch]);

  // Watch assignReward status to toast + close
  useEffect(() => {
    if (assignRewardStatus === "succeeded") {
      toast("Reward assigned to box successfully.", { autoClose: 2500 });
      dispatch(resetAssignRewardStatus());
      onClose();
    } else if (assignRewardStatus === "failed") {
      toast(`Failed to assign reward: ${assignRewardError ?? "Unknown error"}`, { autoClose: 3000 });
      dispatch(resetAssignRewardStatus());
    }
  }, [assignRewardStatus, assignRewardError, dispatch, onClose]);

  // Select handler from ItemSelect
  const handleBoxSelect = (_index: number, selectedBoxName: string | null) => {
    const box = allBoxes.find((b) => b.name === selectedBoxName) || null;
    setSelectedBox(box);
  };

  // Save → assignRewardBox
  const handleSave = () => {
    if (!selectedRewardBox?.box) {
      toast("No reward target selected.", { autoClose: 2500 });
      return;
    }
    if (!selectedBox?.id) {
      toast("Please select a box to assign.", { autoClose: 2500 });
      return;
    }

    dispatch(
      assignRewardBox({
        box_name: selectedRewardBox.box,   // read-only target
        reward_id: String(selectedBox.id) // API expects string
      })
    );
  };

  const isSaving = assignRewardStatus === "loading";

  const mapToOption = (box: Box) => ({
    value: box.name,
    label: `${box.name} $${box.final_value}`,
    imageUrl: box.picture ?? "",
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign Reward to Box">
      <Container>
        <Title>Assign Reward to Box</Title>

        <FieldWrapper>
          <Label>Reward Box (read-only):</Label>
          <ReadOnly>{selectedRewardBox?.box ?? "—"}</ReadOnly>
        </FieldWrapper>

        <FieldWrapper>
          <Label>Select Box:</Label>
          <ItemSelect
            items={allBoxes}
            selectedItemName={selectedBox ? selectedBox.name : null}
            index={0}
            handleItemSelect={handleBoxSelect}
            mapToOption={mapToOption}
          />
        </FieldWrapper>

        {selectedBox && (
          <BoxDetails>
            <img src={getImageSource(selectedBox.picture)} alt={selectedBox.name} />
            <div className="details">
              <div><strong>Name:</strong> {selectedBox.name}</div>
              <div><strong>Price:</strong> ${selectedBox.final_value}</div>
              {selectedBox.description && (
                <div><strong>Description:</strong> {selectedBox.description}</div>
              )}
            </div>
          </BoxDetails>
        )}

        <SaveButton onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </SaveButton>
      </Container>
    </Modal>
  );
};

export default ManageRewardBoxesPage;
